/**
 * @module
 *
 * @description Insurance driver configurations
 */
import { getCanBuyOnline, getCanBuyOnlineAnnotation, getCoverageOptions, getDiscounts, getEaseOfUse, getNAICComplaints, getFinancialStrengthRating, getFinancialStrengthRatingAnnotation, getPoliciesOffered, getPoliciesOfferedAnnotation } from '../selectors/insurance';
import { formatSnakeCase } from '../utils/format';
import { CAN_BUY_ONLINE, POLICIES_OFFERED, VALUE_SIZES } from '../constants/drivers';
/**
 * Get the coverage options driver
 * @param {object} offer - The marketplace offer.
 * @returns {object} - The insurance driver.
 */

export var getDriverCoverageOptions = function getDriverCoverageOptions(offer) {
  return {
    id: 'coverage_options',
    label: 'Coverage options',
    labelTooltip: 'Ratings are based on the number of coverage options a company offers in comparison to other insurers.',
    value: getCoverageOptions(offer) || 'No data',
    valueSize: VALUE_SIZES.MEDIUM
  };
};
/**
 * Get the discounts driver.
 * @param {object} offer - The marketplace offer.
 * @return {object} - The insurance driver.
 */

export var getDriverDiscounts = function getDriverDiscounts(offer) {
  return {
    id: 'discounts',
    label: 'Discounts',
    labelTooltip: 'Ratings are based on the number of discounts a company offers in comparison to other insurers.',
    value: getDiscounts(offer) || 'No data',
    // do not respect 0
    valueSize: VALUE_SIZES.MEDIUM
  };
};
/** Get the ease of use driver.
 * @param {object} offer - The marketplace offer.
 * @return {object} - The insurance driver.
 */

export var getDriverEaseOfUse = function getDriverEaseOfUse(offer) {
  return {
    id: 'ease_of_use',
    label: 'Ease of use',
    labelTooltip: 'Ratings are determined by our editorial team. Our "ease of use" category looks at factors such as website transparency and how easy it is to file a claim.',
    value: getEaseOfUse(offer) || 'No data',
    // do not respect 0
    valueSize: VALUE_SIZES.MEDIUM
  };
};
/** Get the NAIC complaints driver.
 * @param {object} offer - The marketplace offer.
 * @return {object} - The insurance driver.
 */

export var getDriverNAICComplaints = function getDriverNAICComplaints(offer) {
  return {
    id: 'naic_complaints',
    label: 'NAIC complaints',
    labelTooltip: "Ratings are based on complaints to state regulators relative to a company's size, according to three years' worth of data from the National Association of Insurance Commissioners.",
    value: formatSnakeCase(getNAICComplaints(offer)) || 'No data',
    // do not respect 0
    valueSize: VALUE_SIZES.MEDIUM
  };
};
/** Get the can buy online driver.
 * @param {object} offer - The marketplace offer.
 * @return {object} - The insurance driver.
 */

export var getDriverCanBuyOnline = function getDriverCanBuyOnline(offer) {
  return {
    id: 'can_buy_online',
    label: 'Online purchase',
    labelTooltip: 'This indicates whether the company offers a way to apply for and purchase policies entirely online.',
    value: CAN_BUY_ONLINE[getCanBuyOnline(offer)] || 'No data',
    valueSize: VALUE_SIZES.MEDIUM,
    valueTooltip: getCanBuyOnlineAnnotation(offer)
  };
};
/** Get the financial strength rating driver.
 * @param {object} offer - The marketplace offer.
 * @return {object} - The insurance driver.
 */

export var getDriverFinancialStrengthRating = function getDriverFinancialStrengthRating(offer) {
  return {
    id: 'financial_strength_rating',
    label: 'Financial strength rating',
    labelTooltip: "These ratings, which indicate an insurer's ability to pay future claims, are based on assessments from independent agencies such as AM Best, Moody's and Fitch.",
    value: formatSnakeCase(getFinancialStrengthRating(offer)) || 'No data',
    valueSize: VALUE_SIZES.MEDIUM,
    valueTooltip: getFinancialStrengthRatingAnnotation(offer)
  };
};
/** Get the policies offered driver.
 * @param {object} offer - The marketplace offer.
 * @return {object} - The insurance driver.
 */

export var getDriverPoliciesOffered = function getDriverPoliciesOffered(offer) {
  return {
    id: 'policies_offered',
    label: 'Policies offered',
    labelTooltip: "Term policies last a set period of time such as 20 years, while permanent policies last a lifetime. No-exam policies don't require a medical exam.",
    value: POLICIES_OFFERED[getPoliciesOffered(offer)] || 'No data',
    valueSize: VALUE_SIZES.MEDIUM,
    valueTooltip: getPoliciesOfferedAnnotation(offer)
  };
};